import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { GraduationCap, School, User as UserIcon, Loader2 } from 'lucide-react';
import { ACCOUNT_TYPES, type AccountType } from '@eduborg/shared';
import { AuthLayout } from '@/components/AuthLayout';
import { Field } from '@/components/ui/Field';
import { useAuth } from '@/lib/auth-store';
import { api, ApiError } from '@/lib/api';
import { TOKEN_STORAGE_KEY } from '@/lib/auth-store';
import { cn } from '@/lib/utils';

/** ?role= mappings → AccountType. */
const ROLE_PARAM: Record<string, AccountType> = {
  teacher: 'instructor',
  instructor: 'instructor',
  student: 'student',
  school: 'school',
  admin: 'school',
};

/** Show the user-facing wizard option as a friendly label, not the DB enum. */
const ACCOUNT_LABELS: Record<
  AccountType,
  { title: string; subtitle: string; icon: typeof UserIcon }
> = {
  school: {
    title: 'School / Institution',
    subtitle: "We'll set up your district or school workspace.",
    icon: School,
  },
  instructor: {
    title: 'Solo teacher',
    subtitle: 'Get your own branded space + shareable public link.',
    icon: GraduationCap,
  },
  student: {
    title: "I'm a student",
    subtitle: 'Join a class with a code from your teacher.',
    icon: UserIcon,
  },
};

/** Take a free-text org name → URL slug suggestion. */
function suggestSlug(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function SignUp() {
  const { state } = useAuth();
  const [params] = useSearchParams();

  const roleParam = params.get('role');
  const initialAccount: AccountType =
    (roleParam ? ROLE_PARAM[roleParam] : undefined) ?? 'instructor';

  const [accountType, setAccountType] = useState<AccountType>(initialAccount);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Auto-suggest slug from orgName until the user types one explicitly.
  useEffect(() => {
    if (!slugTouched) setOrgSlug(suggestSlug(orgName));
  }, [orgName, slugTouched]);

  const needsOrg = accountType !== 'student';

  const slugPreview = useMemo(
    () => (orgSlug ? `eduborg.com/${orgSlug}` : 'eduborg.com/your-space'),
    [orgSlug],
  );

  if (state.status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.auth.signUp({
        email: email.trim(),
        password,
        fullName: fullName.trim(),
        accountType,
        ...(needsOrg ? { orgName: orgName.trim(), orgSlug } : {}),
      });
      // Store token + redirect; AuthProvider's hydrate will pick it up on next nav.
      localStorage.setItem(TOKEN_STORAGE_KEY, res.token);
      // Hard navigation so AuthProvider re-hydrates with the new token.
      window.location.assign('/dashboard');
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'email_taken') setError('That email already has an account. Sign in?');
        else if (e.code === 'orgSlug_taken')
          setError('That space URL is taken. Try a different one.');
        else if (e.status === 400) {
          // The API returns Zod's flatten() in `details`. Pull the first
          // field error so the user sees WHICH field is wrong, not a
          // generic "check the form" — that was a real complaint.
          const friendly = friendlySignupValidationError(e.details);
          setError(friendly ?? 'Please check the form and try again.');
        } else setError("EduBorg couldn't complete sign-up. Try again in a moment.");
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout eyebrow="Create your account">
      <div className="space-y-2">
        <h1 className="font-display text-3xl font-bold tracking-tight">Get started with EduBorg</h1>
        <p className="text-sm text-muted-foreground">
          Already with us?{' '}
          <Link to="/auth/sign-in" className="font-semibold text-accent hover:underline">
            Sign in
          </Link>
          .
        </p>
      </div>

      {/* Role picker */}
      <div className="grid gap-2.5">
        {ACCOUNT_TYPES.map((t) => {
          const meta = ACCOUNT_LABELS[t];
          const selected = accountType === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setAccountType(t)}
              className={cn(
                'group flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all',
                selected
                  ? 'border-accent bg-accent/5 shadow-sm'
                  : 'border-border bg-card hover:bg-secondary/40',
              )}
              aria-pressed={selected}
            >
              <span
                className={cn(
                  'mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg',
                  selected ? 'bg-accent text-accent-foreground' : 'bg-secondary text-foreground',
                )}
              >
                <meta.icon className="h-4 w-4" aria-hidden />
              </span>
              <span className="flex-1">
                <span className="block font-display text-sm font-semibold">{meta.title}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{meta.subtitle}</span>
              </span>
            </button>
          );
        })}
      </div>

      <form noValidate onSubmit={onSubmit} className="space-y-4">
        <Field
          label="Your full name"
          autoComplete="name"
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />

        <Field
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="At least 8 characters."
        />

        {needsOrg && (
          <>
            <Field
              label={accountType === 'school' ? 'School or institution name' : 'Your space name'}
              required
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              hint={
                accountType === 'school'
                  ? 'e.g. Oakwood High, Westbridge Academy.'
                  : "e.g. Mr. Cato's Biology, Dr. Lin's Algebra."
              }
            />
            <Field
              label="Public URL"
              required
              value={orgSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setOrgSlug(suggestSlug(e.target.value));
              }}
              hint={slugPreview}
            />
          </>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:translate-y-[-1px] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          {submitting
            ? 'Creating your space…'
            : accountType === 'student'
              ? 'Create account'
              : 'Create my space'}
        </button>

        <p className="text-center text-xs text-muted-foreground">
          By signing up you agree to our{' '}
          <a href="/terms" className="underline hover:text-foreground">
            Terms
          </a>{' '}
          and{' '}
          <a href="/privacy" className="underline hover:text-foreground">
            Privacy
          </a>
          .
        </p>
      </form>
    </AuthLayout>
  );
}

export default SignUp;

/**
 * Map Zod's `flatten()` shape into a user-readable single-sentence error.
 * Picks the first failing field with a friendly label. Returns null if the
 * payload doesn't look like a flatten() result, so callers can fall back to
 * a generic message.
 */
function friendlySignupValidationError(details: unknown): string | null {
  const d = details as
    | { fieldErrors?: Record<string, string[] | undefined>; formErrors?: string[] }
    | undefined;
  if (!d || typeof d !== 'object') return null;
  const labels: Record<string, string> = {
    email: 'Email',
    password: 'Password',
    fullName: 'Full name',
    accountType: 'Account type',
    orgName: 'Space name',
    orgSlug: 'Public URL',
  };
  const fieldErrors = d.fieldErrors ?? {};
  for (const [key, errs] of Object.entries(fieldErrors)) {
    const first = errs?.[0];
    if (!first) continue;
    const label = labels[key] ?? key;
    // Tighten common Zod messages.
    if (/at least 8/i.test(first) || /must contain at least 8/i.test(first)) {
      return `${label} must be at least 8 characters.`;
    }
    if (/required/i.test(first) || /^Required$/.test(first)) {
      return `${label} is required.`;
    }
    if (/email/i.test(first)) return `${label} doesn't look right.`;
    return `${label}: ${first}`;
  }
  if (d.formErrors?.length) return d.formErrors[0]!;
  return null;
}
